import type { DesktopShareDropdownProps } from "./DesktopShareDropdown.types";

import { buildShareableViewJobUrl, type ShareSource } from "./buildShareableViewJobUrl";
import { ShareMenuRow } from "./ShareMenuRow";
import { Box, type BoxRef, Button, Pressable, Share } from "../../../../../../mads";
import { Link, Mail, SMS, WhatsApp } from "../../../../../../mads/icons";
import { useI18n } from "../../../../../../shared/I18n";
import { logTapButton } from "../../../../../../shared/native-modules/tare/logTapButton";
import { useEscapeKey } from "../../../../../../shared/util/useWebModalAccessibility";
import { useViewjobOneGraphModel } from "../../../../context/ViewJobDataContext";

import { colors } from "@indeed/ifl-tokens/react-native";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AccessibilityInfo, StyleSheet } from "react-native";

interface AnchorPosition {
  top: number;
  left: number;
}

const PANEL_ID = "desktop-share-dropdown-panel";
const NAV_KEYS = new Set(["ArrowDown", "ArrowUp", "Home", "End"]);

export function DesktopShareDropdown({
  jobKey,
  screenName = "rn-view-job",
}: DesktopShareDropdownProps): React.JSX.Element | null {
  const i18n = useI18n();
  const model = useViewjobOneGraphModel();
  const [isOpen, setIsOpen] = useState(false);
  const [anchorPosition, setAnchorPosition] = useState<AnchorPosition | null>(null);
  const [focusedIdx, setFocusedIdx] = useState(0);
  const triggerRef = useRef<BoxRef>(null);
  const panelRef = useRef<BoxRef>(null);
  const itemRefs = useRef<Array<BoxRef | null>>([]);

  const shareUrls = useMemo(
    () => ({
      copy: buildShareableViewJobUrl(jobKey, "copy"),
      email: buildShareableViewJobUrl(jobKey, "email"),
      sms: buildShareableViewJobUrl(jobKey, "sms"),
      whatsapp: buildShareableViewJobUrl(jobKey, "whatsapp"),
    }),
    [jobKey],
  );

  const hideWhatsApp = model?.features?.country === "JP";
  const itemCount = hideWhatsApp ? 3 : 4;

  const handleDismiss = useCallback(() => {
    setIsOpen(false);
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    (triggerRef.current as unknown as HTMLElement | null)?.focus?.();
  }, []);

  // Document-level Escape so the menu closes regardless of which item has focus.
  // handleDismiss has empty deps, so the ref captures it once and never goes stale.
  const onCloseRef = useRef(handleDismiss);
  useEscapeKey(onCloseRef, isOpen);

  // MADS Button doesn't forward aria-haspopup/aria-controls; set them on the
  // underlying DOM node directly. aria-expanded is driven via accessibilityState.
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    const el = triggerRef.current as unknown as HTMLElement | null;
    if (!el) return;
    el.setAttribute("aria-haspopup", "menu");
    el.setAttribute("aria-controls", PANEL_ID);
  }, []);

  useEffect(() => {
    if (isOpen) setFocusedIdx(0);
  }, [isOpen]);

  // Roving tabindex + initial focus into the menu when it opens.
  useEffect(() => {
    if (!isOpen) return;
    const raf = requestAnimationFrame(() => {
      itemRefs.current.slice(0, itemCount).forEach((el, i) => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        const node = el as unknown as HTMLElement | null;
        if (!node) return;
        node.tabIndex = i === focusedIdx ? 0 : -1;
      });
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      const target = itemRefs.current[focusedIdx] as unknown as HTMLElement | null;
      target?.focus?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [isOpen, focusedIdx, itemCount]);

  // Close the menu when focus leaves it entirely.
  useEffect(() => {
    if (!isOpen) return;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    const panel = panelRef.current as unknown as HTMLElement | null;
    if (!panel) return;
    const handleFocusOut = (e: FocusEvent) => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      const next = e.relatedTarget as Node | null;
      if (next && panel.contains(next)) return;
      setIsOpen(false);
    };
    panel.addEventListener("focusout", handleFocusOut);
    return () => panel.removeEventListener("focusout", handleFocusOut);
  }, [isOpen]);

  // Arrow / Home / End / Tab handling on the panel.
  useEffect(() => {
    if (!isOpen) return;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    const panel = panelRef.current as unknown as HTMLElement | null;
    if (!panel) return;
    const handleKey = (e: KeyboardEvent) => {
      // Tab and Shift+Tab close the menu and return focus to the trigger.
      // The portal places the panel at the end of the document, so the
      // browser's natural tab order would otherwise jump to page tail.
      if (e.key === "Tab") {
        e.preventDefault();
        handleDismiss();
        return;
      }
      if (!NAV_KEYS.has(e.key)) return;
      e.preventDefault();
      setFocusedIdx((current) => {
        switch (e.key) {
          case "ArrowDown":
            return (current + 1) % itemCount;
          case "ArrowUp":
            return (current - 1 + itemCount) % itemCount;
          case "Home":
            return 0;
          case "End":
            return itemCount - 1;
          default:
            return current;
        }
      });
    };
    panel.addEventListener("keydown", handleKey);
    return () => panel.removeEventListener("keydown", handleKey);
  }, [handleDismiss, isOpen, itemCount]);

  const handleTriggerPress = useCallback(() => {
    logTapButton(screenName, "share").catch(console.error);
    if (!isOpen) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      const el = triggerRef.current as unknown as HTMLElement | null;
      const rect = el?.getBoundingClientRect?.();
      if (rect) {
        setAnchorPosition({
          top: rect.bottom + 4,
          left: rect.left,
        });
      }
    }
    setIsOpen((open) => !open);
  }, [isOpen, screenName]);

  const openExternal = useCallback(
    (href: string, src: ShareSource) => {
      logTapButton(screenName, `share-${src}`).catch(console.error);
      window.open(href, "_blank", "noopener,noreferrer");
      handleDismiss();
    },
    [handleDismiss, screenName],
  );

  const copyLink = useCallback(() => {
    logTapButton(screenName, "share-copy").catch(console.error);
    handleDismiss();
    navigator.clipboard
      .writeText(shareUrls.copy)
      .then(() => AccessibilityInfo.announceForAccessibility(i18n.gettext("Link copied")))
      .catch((error: unknown) => {
        console.error("Copy link failed:", error);
        AccessibilityInfo.announceForAccessibility(i18n.gettext("Unable to copy link"));
      });
  }, [handleDismiss, i18n, screenName, shareUrls.copy]);

  if (model?.shareModel == null) {
    return null;
  }

  const setItemRef = (idx: number) => (el: BoxRef | null) => {
    itemRefs.current[idx] = el;
  };

  const overlay =
    isOpen && anchorPosition ? (
      <>
        <Pressable
          onPress={handleDismiss}
          accessible={false}
          importantForAccessibility="no-hide-descendants"
          testID="desktop-share-dropdown-backdrop"
          sx={styles.backdrop}
        />
        <Box
          ref={panelRef}
          nativeID={PANEL_ID}
          sx={[
            styles.panel,
            {
              top: anchorPosition.top,
              left: anchorPosition.left,
            },
          ]}
          accessibilityRole="menu"
          accessibilityLabel={i18n.gettext("Share job")}
          testID="desktop-share-dropdown-panel"
        >
          <ShareMenuRow
            ref={setItemRef(0)}
            label={i18n.pgettext("Copy viewjob link to clipboard", "Copy link")}
            icon={<Link aria-hidden />}
            onActivate={copyLink}
            testID="desktop-share-dropdown-copy"
          />
          <ShareMenuRow
            ref={setItemRef(1)}
            label={i18n.pgettext("Email viewjob link", "Email")}
            icon={<Mail aria-hidden />}
            onActivate={() =>
              openExternal(
                `mailto:?subject=${encodeURIComponent(
                  i18n.gettext("Job Posting"),
                )}&body=${encodeURIComponent(shareUrls.email)}`,
                "email",
              )
            }
            testID="desktop-share-dropdown-email"
          />
          <ShareMenuRow
            ref={setItemRef(2)}
            label={i18n.pgettext("Text viewjob link", "Text message")}
            icon={<SMS aria-hidden />}
            onActivate={() => openExternal(`sms:?body=${encodeURIComponent(shareUrls.sms)}`, "sms")}
            testID="desktop-share-dropdown-sms"
          />
          {!hideWhatsApp && (
            <ShareMenuRow
              ref={setItemRef(3)}
              label={i18n.pgettext("Share viewjob link on WhatsApp", "WhatsApp")}
              icon={<WhatsApp aria-hidden />}
              onActivate={() =>
                openExternal(
                  `https://web.whatsapp.com/send?text=${encodeURIComponent(shareUrls.whatsapp)}`,
                  "whatsapp",
                )
              }
              testID="desktop-share-dropdown-whatsapp"
            />
          )}
        </Box>
      </>
    ) : null;

  // Portal into document.body so the panel escapes ancestor stacking contexts.
  const portaled = overlay ? createPortal(overlay, document.body) : null;

  return (
    <Box sx={styles.anchor}>
      <Button
        ref={triggerRef}
        variant="utility"
        testID="desktop-share-dropdown-trigger"
        onPress={handleTriggerPress}
        ariaLabel={i18n.gettext("Share job")}
        accessibilityState={{ expanded: isOpen }}
        iconOnly
        size="md"
        iconAfter={<Share color={colors.foregroundStrong} />}
      />
      {portaled}
    </Box>
  );
}

const styles = StyleSheet.create({
  anchor: {
    position: "relative",
  },
  backdrop: {
    // @ts-expect-error - position 'fixed' is valid on web (react-native-web)
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "transparent",
    zIndex: 9999,
  },
  panel: {
    // @ts-expect-error - position 'fixed' is valid on web (react-native-web)
    position: "fixed",
    minWidth: 220,
    backgroundColor: colors.background,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.borderStrong,
    overflow: "hidden",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
    zIndex: 10000,
  },
});
